// ============================================================
// DM-LMS — useJustVerifiedPayment Hook
// Watches a student's payment list (as it polls) and reports the code of a payment that
// just turned `success`, until dismissed or PAYMENT_BANNER_DISMISS_MS passes.
// The transition rule itself is findNewlyVerifiedPayments() in shared utils.
// ============================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { findNewlyVerifiedPayments } from "@dm-lms/shared/utils";
import { PAYMENT_BANNER_DISMISS_MS } from "@dm-lms/shared/constants";
import type { PaymentWithCourse } from "@dm-lms/shared/types";

export function useJustVerifiedPayment(payments: readonly PaymentWithCourse[] | null) {
  const previous = useRef<readonly PaymentWithCourse[] | null>(null);
  const [justVerifiedCode, setJustVerifiedCode] = useState<string | null>(null);

  // The first list seen is only a baseline — opening the page with a paid payment is not news.
  useEffect(() => {
    if (payments === null) return;
    if (previous.current !== null) {
      const [verified] = findNewlyVerifiedPayments(previous.current, payments);
      if (verified) setJustVerifiedCode(verified.code);
    }
    previous.current = payments;
  }, [payments]);

  useEffect(() => {
    if (justVerifiedCode === null) return;
    const timer = setTimeout(() => { setJustVerifiedCode(null); }, PAYMENT_BANNER_DISMISS_MS);
    return () => { clearTimeout(timer); };
  }, [justVerifiedCode]);

  const dismiss = useCallback(() => { setJustVerifiedCode(null); }, []);

  return { justVerifiedCode, dismiss };
}
